import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bot,
  CheckCircle2,
  Coins,
  ExternalLink,
  Headphones,
  History,
  LockKeyhole,
  Plus,
  Send,
  ShieldCheck,
  Sparkles
} from "lucide-react";
import {
  askTeknoparkAI,
  purchaseTeknoparkAIWithCredits,
  requestAIHumanSupport,
  subscribeAIConversationMessages,
  subscribeAIConversations,
  subscribeTenantAIState
} from "../services/teknoparkAIService";

const suggestedQuestions = [
  "Ar-Ge personeli uzaktan çalışabilir mi?",
  "Proje süresi uzatımı için hangi belgeler gerekir?",
  "Muafiyet günleri nasıl hesaplanır?",
  "Proje bitirme belgesi ne zaman alınmalıdır?"
];

const humanSupportCreditCost = 50;

const welcomeMessages = [
  {
    id: "welcome",
    role: "assistant",
    text: "Merhaba, ben Teknopark AI. Yayımlanmış ve doğrulanmış Ar-Ge / teknopark bilgi havuzuna dayanarak sorularınızı yanıtlarım."
  }
];

const isEntitlementActive = (entitlement) => {
  if (entitlement?.status !== "active") return false;
  const expiresAt = entitlement.expiresAt?.toDate?.();
  return !expiresAt || expiresAt.getTime() > Date.now();
};

const formatDate = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "Süresiz";
  return new Intl.DateTimeFormat("tr-TR", { dateStyle: "long" }).format(date);
};

const getErrorMessage = (error) => {
  const code = String(error?.code || "");
  const rawMessage = error?.message?.replace(/^Firebase:\s*/i, "") || "";
  if (code.includes("resource-exhausted")) return "Bu ayki soru kotanız doldu.";
  if (code.includes("failed-precondition") && /kredi/i.test(rawMessage)) return rawMessage;
  if (code.includes("failed-precondition")) return "Teknopark AI modülünüz aktif değil.";
  if (code.includes("permission-denied")) return "Bu müşteri portalı için erişim doğrulanamadı.";
  return rawMessage || "Yanıt alınamadı. Lütfen tekrar deneyin.";
};

function TeknoparkAIView({ tenantId, user, themeMode = "light", onOpenSupport }) {
  const [state, setState] = useState({ module: null, entitlement: null, wallet: null });
  const [messages, setMessages] = useState(welcomeMessages);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState("");
  const [newConversationMode, setNewConversationMode] = useState(false);
  const [requestingSupport, setRequestingSupport] = useState(false);
  const [supportTicketId, setSupportTicketId] = useState("");
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);
  const dark = themeMode === "dark";
  const colors = {
    text: dark ? "#e5edf6" : "#123f50",
    muted: dark ? "#9fb0c4" : "#64748b",
    surface: dark ? "#162133" : "#ffffff",
    subtle: dark ? "#203047" : "#f8fafc",
    border: dark ? "#334155" : "#dbe2ea"
  };

  useEffect(() => {
    const stop = subscribeTenantAIState(
      tenantId,
      setState,
      (reason) => setError(getErrorMessage(reason))
    );
    return stop;
  }, [tenantId]);

  useEffect(() => {
    const stop = subscribeAIConversations({
      tenantId,
      userId: user?.uid,
      onData: (items) => {
        setConversations(items);
        setSelectedConversationId((current) => {
          if (newConversationMode) return "";
          if (current) return current;
          return items[0]?.id || "";
        });
      },
      onError: (reason) => setError(getErrorMessage(reason))
    });
    return stop;
  }, [newConversationMode, tenantId, user?.uid]);

  useEffect(() => {
    const selected = conversations.find((item) => item.id === selectedConversationId);
    setSupportTicketId(selected?.escalationTicketId || "");
  }, [conversations, selectedConversationId]);

  useEffect(() => {
    if (!selectedConversationId) {
      setMessages(welcomeMessages);
      return () => {};
    }
    return subscribeAIConversationMessages({
      tenantId,
      conversationId: selectedConversationId,
      onData: (items) => setMessages(items.length ? items : welcomeMessages),
      onError: (reason) => setError(getErrorMessage(reason))
    });
  }, [selectedConversationId, tenantId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, busy]);

  const entitlementActive = isEntitlementActive(state.entitlement);
  const balance = Number(state.wallet?.balance || 0);
  const price = Number(state.module?.creditPrice || 1000);
  const canPurchase = state.module?.isActive !== false && balance >= price;
  const usage = useMemo(() => ({
    used: Number(state.entitlement?.usedQuestions || 0),
    limit: Number(state.entitlement?.questionLimit || state.module?.questionLimit || 250)
  }), [state.entitlement, state.module]);

  const purchase = async () => {
    const confirmed = window.appConfirm
      ? await window.appConfirm(
        `${price.toLocaleString("tr-TR")} kredi kullanılarak Teknopark AI ${Number(state.module?.durationDays || 30)} günlüğüne etkinleştirilecek.`,
        { title: "Teknopark AI'ı etkinleştir", type: "info", confirmText: "Krediyle etkinleştir" }
      )
      : window.confirm("Teknopark AI kredi bakiyenizden satın alınsın mı?");
    if (!confirmed) return;
    setPurchasing(true);
    setError("");
    try {
      await purchaseTeknoparkAIWithCredits(tenantId);
    } catch (reason) {
      setError(getErrorMessage(reason));
    } finally {
      setPurchasing(false);
    }
  };

  const submitQuestion = async (event, suggestedQuestion = "") => {
    event?.preventDefault?.();
    const text = String(suggestedQuestion || question).trim();
    if (!text || busy || !entitlementActive) return;
    const userMessage = { id: `user-${Date.now()}`, role: "user", text };
    setMessages((current) => [...current, userMessage]);
    setQuestion("");
    setBusy(true);
    setError("");
    try {
      const response = await askTeknoparkAI({
        tenantId,
        question: text,
        conversationId: selectedConversationId
      });
      setSelectedConversationId(response.conversationId);
      setNewConversationMode(false);
      if (response.usage) {
        setState((current) => ({
          ...current,
          entitlement: {
            ...(current.entitlement || {}),
            usedQuestions: response.usage.used,
            questionLimit: response.usage.limit
          }
        }));
      }
    } catch (reason) {
      const message = getErrorMessage(reason);
      setError(message);
      setMessages((current) => [...current, {
        id: `error-${Date.now()}`,
        role: "assistant",
        text: message,
        isError: true
      }]);
    } finally {
      setBusy(false);
    }
  };

  const startNewConversation = () => {
    setNewConversationMode(true);
    setSelectedConversationId("");
    setSupportTicketId("");
    setMessages(welcomeMessages);
    setQuestion("");
    setError("");
  };

  const selectConversation = (conversation) => {
    setNewConversationMode(false);
    setSelectedConversationId(conversation.id);
    setSupportTicketId(conversation.escalationTicketId || "");
    setError("");
  };

  const requestHumanSupport = async () => {
    if (!selectedConversationId || requestingSupport) return;
    const confirmed = window.appConfirm
      ? await window.appConfirm(
        `Bu konuşmanın dökümü Destek Merkezi'ne aktarılacak ve bakiyenizden ${humanSupportCreditCost} kredi düşülecek.`,
        { title: "Canlı desteğe bağlan", type: "info", confirmText: `${humanSupportCreditCost} krediyle bağlan` }
      )
      : window.confirm(`Bu konuşmayı ${humanSupportCreditCost} kredi karşılığında Argevio destek ekibine aktarmak istiyor musunuz?`);
    if (!confirmed) return;

    setRequestingSupport(true);
    setError("");
    try {
      const response = await requestAIHumanSupport({
        tenantId,
        conversationId: selectedConversationId
      });
      setSupportTicketId(response.ticketId);
      if (Number.isFinite(Number(response.balance))) {
        setState((current) => ({
          ...current,
          wallet: {
            ...(current.wallet || {}),
            balance: Number(response.balance)
          }
        }));
      }
      onOpenSupport?.(response.ticketId);
    } catch (reason) {
      setError(getErrorMessage(reason));
    } finally {
      setRequestingSupport(false);
    }
  };

  if (!tenantId) {
    return <div style={{ padding: 28, borderRadius: 16, background: "#fee2e2", color: "#991b1b" }}>Müşteri portalı eşleştirilemedi. Lütfen yönetim ekibiyle iletişime geçin.</div>;
  }

  return (
    <section style={{ color: colors.text }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ width: 48, height: 48, borderRadius: 15, display: "grid", placeItems: "center", background: "linear-gradient(135deg,#0f766e,#0ea5a4)", color: "#fff", boxShadow: "0 10px 24px rgba(15,118,110,.2)" }}><Bot size={25}/></span>
          <div>
            <h2 style={{ margin: 0 }}>Teknopark AI</h2>
            <p style={{ margin: "5px 0 0", color: colors.muted, fontWeight: 650 }}>Teknopark mevzuatı ve süreçleri için doğrulanmış bilgi asistanı</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
          <span style={topBadge(colors)}><Coins size={16}/>{balance.toLocaleString("tr-TR")} kredi</span>
          {entitlementActive && <span style={{ ...topBadge(colors), background: dark ? "#123f38" : "#dcfce7", color: dark ? "#a7f3d0" : "#166534" }}><CheckCircle2 size={16}/> Aktif · {Math.max(0, usage.limit - usage.used)} soru kaldı</span>}
        </div>
      </div>

      {error && <div style={{ padding: 11, borderRadius: 11, background: "#fee2e2", color: "#991b1b", marginBottom: 13, fontWeight: 700 }}>{error}</div>}

      {!entitlementActive ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.25fr) minmax(280px,.75fr)", gap: 18 }}>
          <article style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 20, padding: 26 }}>
            <span style={{ width: 48, height: 48, borderRadius: 15, display: "grid", placeItems: "center", background: dark ? "#29374c" : "#eef4f6", color: colors.text }}><LockKeyhole size={23}/></span>
            <h3 style={{ fontSize: 24, margin: "17px 0 8px" }}>Teknopark süreçlerinde hızlı ve güvenli cevaplar</h3>
            <p style={{ color: colors.muted, lineHeight: 1.65, maxWidth: 680 }}>
              Argevio ekibi tarafından doğrulanıp yayımlanan soru-cevap havuzunda arama yapar. Eşleşen doğrulanmış bilgi yoksa tahmin üretmez ve sizi Destek Merkezi'ne yönlendirir.
            </p>
            <div style={{ display: "grid", gap: 10, marginTop: 20 }}>
              {[
                "Kategori, alternatif soru ve etiketlerle zenginleştirilmiş bilgi havuzu",
                "Kaynak ve geçerlilik tarihiyle sunulan cevaplar",
                "Müşteri verilerinden kesin biçimde ayrılmış kullanım kayıtları"
              ].map((text) => <div key={text} style={{ display: "flex", gap: 9, alignItems: "center" }}><ShieldCheck size={18} color="#0f766e"/><span>{text}</span></div>)}
            </div>
          </article>
          <aside style={{ background: "linear-gradient(145deg,#123f50,#0f766e)", color: "#fff", borderRadius: 20, padding: 24, display: "flex", flexDirection: "column" }}>
            <Sparkles size={25}/>
            <h3 style={{ fontSize: 21, margin: "14px 0 6px" }}>Modülü etkinleştir</h3>
            <p style={{ opacity: .78, lineHeight: 1.55, margin: "0 0 18px" }}>{Number(state.module?.durationDays || 30)} günlük kullanım ve ayda {Number(state.module?.questionLimit || 250)} soru.</p>
            <div style={{ marginTop: "auto" }}>
              <small style={{ opacity: .7 }}>Modül bedeli</small>
              <div style={{ fontSize: 29, fontWeight: 950, marginTop: 3 }}>{price.toLocaleString("tr-TR")} <span style={{ fontSize: 15 }}>kredi</span></div>
              <button disabled={!canPurchase || purchasing} onClick={purchase} style={{ ...purchaseButton, opacity: !canPurchase || purchasing ? .55 : 1 }}>
                {purchasing ? "Etkinleştiriliyor..." : "Krediyle etkinleştir"}
              </button>
              {!canPurchase && <small style={{ display: "block", marginTop: 10, opacity: .76 }}>Bakiyeniz yetersiz. Kredi tanımlanması için Argevio ekibiyle iletişime geçin.</small>}
            </div>
          </aside>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 280px", gap: 16 }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 20, overflow: "hidden", minHeight: 620, display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 17px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
              <div><strong>Teknopark AI Asistanı</strong><div style={{ color: colors.muted, fontSize: 12, marginTop: 3 }}>Doğrulanmış bilgi havuzu · Yanıtlar kaynak kayıtlarıyla eşleştirilir</div></div>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                {supportTicketId ? (
                  <button
                    onClick={() => onOpenSupport?.(supportTicketId)}
                    style={supportButton(colors, true)}
                  >
                    <CheckCircle2 size={15}/> Canlı destek görüşmesini aç
                  </button>
                ) : (
                  <button
                    onClick={requestHumanSupport}
                    disabled={!selectedConversationId || requestingSupport}
                    style={{ ...supportButton(colors), opacity: !selectedConversationId || requestingSupport ? .5 : 1 }}
                  >
                    <Headphones size={15}/> {requestingSupport ? "Bağlanıyor..." : `Canlı desteğe bağlan · ${humanSupportCreditCost} kredi`}
                  </button>
                )}
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 0 4px rgba(34,197,94,.12)" }}/>
              </div>
            </div>
            <div style={{ flex: 1, padding: 18, display: "flex", flexDirection: "column", gap: 13, overflowY: "auto", maxHeight: 500 }}>
              {messages.map((message) => (
                <div key={message.id} style={{ alignSelf: message.role === "user" ? "flex-end" : "flex-start", maxWidth: "82%" }}>
                  <div style={{
                    padding: "12px 14px",
                    borderRadius: message.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    background: message.role === "user" ? "#0f766e" : message.isError ? "#fee2e2" : colors.subtle,
                    color: message.role === "user" ? "#fff" : message.isError ? "#991b1b" : colors.text,
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap"
                  }}>
                    {message.text}
                  </div>
                  {message.category && <small style={{ display: "block", marginTop: 5, color: colors.muted }}>Kategori: {message.category}</small>}
                  {message.sources?.map((source, index) => (
                    source.url
                      ? <a key={`${source.url}-${index}`} href={source.url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 7, marginRight: 7, color: "#0f766e", fontSize: 12, fontWeight: 800 }}><ExternalLink size={13}/>{source.title || "Kaynak"}</a>
                      : <small key={`${source.title}-${index}`} style={{ display: "block", marginTop: 7, color: colors.muted }}>Kaynak: {source.title}</small>
                  ))}
                  {message.role === "assistant" && message.id !== "welcome" && !supportTicketId && (
                    <button
                      onClick={requestHumanSupport}
                      disabled={requestingSupport}
                      style={{ marginTop: 8, border: 0, background: "transparent", color: "#0f766e", fontWeight: 850, cursor: "pointer", padding: 0 }}
                    >
                      {message.answered === false ? "Uzman desteği iste" : "Bu yanıt yardımcı olmadı"} · {humanSupportCreditCost} kredi →
                    </button>
                  )}
                </div>
              ))}
              {busy && <div style={{ alignSelf: "flex-start", padding: "11px 14px", borderRadius: "16px 16px 16px 4px", background: colors.subtle, color: colors.muted }}>Doğrulanmış cevap aranıyor…</div>}
              <div ref={endRef}/>
            </div>
            <form onSubmit={submitQuestion} style={{ padding: 14, borderTop: `1px solid ${colors.border}`, display: "flex", gap: 9 }}>
              <textarea
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    submitQuestion(event);
                  }
                }}
                placeholder="Teknopark süreciyle ilgili sorunuzu yazın..."
                rows={2}
                style={{ flex: 1, resize: "none", border: `1px solid ${colors.border}`, background: colors.subtle, color: colors.text, borderRadius: 12, padding: "11px 12px", font: "inherit" }}
              />
              <button disabled={busy || !question.trim()} type="submit" aria-label="Soruyu gönder" style={{ width: 48, border: 0, borderRadius: 12, background: "#0f766e", color: "#fff", display: "grid", placeItems: "center", cursor: "pointer", opacity: busy || !question.trim() ? .55 : 1 }}><Send size={19}/></button>
            </form>
          </div>
          <aside style={{ display: "grid", alignContent: "start", gap: 13 }}>
            <div style={{ padding: 16, borderRadius: 16, background: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <strong style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><History size={16}/> Konuşma geçmişi</strong>
                <button onClick={startNewConversation} title="Yeni konuşma" aria-label="Yeni konuşma" style={{ width: 30, height: 30, borderRadius: 9, border: `1px solid ${colors.border}`, background: colors.subtle, color: colors.text, display: "grid", placeItems: "center", cursor: "pointer" }}><Plus size={16}/></button>
              </div>
              <div style={{ display: "grid", gap: 7, marginTop: 11, maxHeight: 210, overflowY: "auto" }}>
                {conversations.length === 0 && <small style={{ color: colors.muted, lineHeight: 1.45 }}>İlk sorunuzu gönderdiğinizde konuşmanız burada saklanır.</small>}
                {conversations.map((conversation) => {
                  const selected = conversation.id === selectedConversationId;
                  return (
                    <button
                      key={conversation.id}
                      onClick={() => selectConversation(conversation)}
                      style={{
                        textAlign: "left",
                        padding: "9px 10px",
                        borderRadius: 10,
                        border: `1px solid ${selected ? "#0f766e" : colors.border}`,
                        background: selected ? (dark ? "#123f38" : "#ecfdf5") : colors.subtle,
                        color: colors.text,
                        cursor: "pointer"
                      }}
                    >
                      <span style={{ display: "block", fontWeight: 800, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{conversation.title || "Teknopark AI görüşmesi"}</span>
                      <small style={{ display: "flex", justifyContent: "space-between", gap: 6, marginTop: 4, color: colors.muted }}>
                        <span>{formatShortDate(conversation.updatedAt)}</span>
                        {conversation.status === "escalated" && <span style={{ color: "#0f766e", fontWeight: 800 }}>Destekte</span>}
                      </small>
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ padding: 16, borderRadius: 16, background: colors.surface, border: `1px solid ${colors.border}` }}>
              <strong>Örnek sorular</strong>
              <div style={{ display: "grid", gap: 8, marginTop: 11 }}>
                {suggestedQuestions.map((item) => <button key={item} disabled={busy} onClick={(event) => submitQuestion(event, item)} style={{ textAlign: "left", padding: 10, borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.subtle, color: colors.text, cursor: "pointer", lineHeight: 1.35 }}>{item}</button>)}
              </div>
            </div>
            <div style={{ padding: 16, borderRadius: 16, background: colors.surface, border: `1px solid ${colors.border}` }}>
              <small style={{ color: colors.muted }}>Lisans bitişi</small>
              <strong style={{ display: "block", marginTop: 4 }}>{formatDate(state.entitlement?.expiresAt)}</strong>
              <div style={{ height: 7, borderRadius: 999, background: colors.subtle, marginTop: 14, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min(100, (usage.used / Math.max(1, usage.limit)) * 100)}%`, background: "#0f766e" }}/>
              </div>
              <small style={{ color: colors.muted, display: "block", marginTop: 7 }}>{usage.used} / {usage.limit} soru kullanıldı</small>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}

const topBadge = (colors) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 11px",
  borderRadius: 999,
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  color: colors.text,
  fontWeight: 850,
  fontSize: 13
});

const purchaseButton = {
  width: "100%",
  marginTop: 17,
  border: 0,
  borderRadius: 11,
  padding: "12px 14px",
  background: "#ffffff",
  color: "#0f5f5a",
  fontWeight: 900,
  cursor: "pointer"
};

const supportButton = (colors, active = false) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  border: `1px solid ${active ? "#86efac" : colors.border}`,
  borderRadius: 10,
  padding: "8px 10px",
  background: active ? "#dcfce7" : colors.subtle,
  color: active ? "#166534" : colors.text,
  fontWeight: 850,
  fontSize: 12,
  cursor: "pointer"
});

const formatShortDate = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};

export default TeknoparkAIView;
